"use strict";
/**
 * Sharing one type of data at a time with a connection, over the CMC scope-update path.
 *
 * The person (patient side, personal token) changes what a relationship's data grant shares by
 * writing a `consent/scope-update-cmc` trigger on the relationship's own collectors stream
 * (`CmcRelationship.localCollectorStreamId`). The platform's CMC plugin applies the new
 * permission set to the grant (keeping its own `:_cmc:*` channel permissions), records the
 * outcome on the trigger (`accessId`, `newPermissions`, `applied`, `status`) and delivers the
 * content to the connection. What the connection already read stays with it: only what comes
 * next is stopped.
 *
 * HDS adds what the connection should be told, inside the HDS-owned sub-envelope
 * `content.hds` (the same convention as the data-export alerts, data-model
 * `documentation/CUSTOM-FIELDS-AND-SYSTEM.md`), never as new top-level content fields:
 *
 *   content.hds = { removed?: string[], added?: string[], asked?: string[], message?: localizable text }
 *
 * - `removed`: the streams the person stopped sharing (`stopSharingStream`);
 * - `added`: the streams the person shared again (`shareStreamsAgain`);
 * - `asked`: on an answer to a connection's request, the streams it asked for that were not
 *   shared (read only here: `cmc.acceptScopeUpdate` / `refuseScopeUpdate` write a fixed content);
 * - `message`: the notice for the connection, per language.
 *
 * A connection asking for data again sends a `consent/scope-request-cmc` (`cmc.proposeScopeUpdate`);
 * the person answers it with `cmc.acceptScopeUpdate` / `cmc.refuseScopeUpdate`, not with these
 * helpers. `listSharingNotes` reads all of it back as one list for the conversation.
 *
 * A connection's updated data set travels in a scope request too (data-model 3.41.0):
 *
 *   content.hds = { formSpec: FormSpec (never `openLink`), formSpecHash: 'sha256:…' }
 *
 * `proposeDataSetUpdate` writes it (connection side) and `listScopeRequests` reads the requests
 * back with their answers; `acceptDataSetUpdate` accepts one and replaces the person's copy of the
 * data set on her `consent/accept-cmc` event (person side).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DATASET_REQUEST_TTL_SEC = exports.ET_SCOPE_REQUEST = exports.ET_SCOPE_UPDATE = void 0;
exports.personPermissions = personPermissions;
exports.planStopSharing = planStopSharing;
exports.scopeDiff = scopeDiff;
exports.stopSharingStream = stopSharingStream;
exports.shareStreamsAgain = shareStreamsAgain;
exports.listSharingNotes = listSharingNotes;
exports.parseSharingNotes = parseSharingNotes;
exports.proposeDataSetUpdate = proposeDataSetUpdate;
exports.listScopeRequests = listScopeRequests;
exports.parseScopeRequests = parseScopeRequests;
exports.acceptedDataSetHash = acceptedDataSetHash;
exports.acceptDataSetUpdate = acceptDataSetUpdate;
exports.uncoveredPermissions = uncoveredPermissions;
const HDSModelInitAndSingleton_ts_1 = require("../HDSModel/HDSModelInitAndSingleton.js");
const errors_ts_1 = require("../errors.js");
const patchedPryv_ts_1 = require("../patchedPryv.js");
const formSpec_ts_1 = require("./formSpec.js");
exports.ET_SCOPE_UPDATE = 'consent/scope-update-cmc';
exports.ET_SCOPE_REQUEST = 'consent/scope-request-cmc';
/** A data stream the person would recognise as shared data (not a platform stream, not the chat-only placeholder). */
function isDataStream(streamId) {
    return streamId != null && !streamId.startsWith(':_') && streamId !== 'hds-noop';
}
/** The stream ids above `streamId` in the data model and in `parentIds`, plus `*`. */
function streamsAbove(streamId, parentIds = []) {
    const above = new Set(['*', ...parentIds]);
    try {
        const streams = (0, HDSModelInitAndSingleton_ts_1.getModel)().streams;
        for (const id of streams.getParentsIds(streamId, false)) {
            if (id !== streamId)
                above.add(id);
        }
        // `*` never reaches an explicitGrantOnly root (data-model 3.43.0): only a grant naming it does
        if (streams.isExplicitGrantOnly(streamId))
            above.delete('*');
    }
    catch { /* model not loaded or stream unknown: only `*` */ }
    return above;
}
/**
 * The permissions the person decides on: every permission but the platform's own `:_cmc:*`
 * channel ones, in the shape `consent/scope-update-cmc` takes (`{ streamId, level }` or
 * `{ feature, setting }`).
 */
function personPermissions(perms) {
    return (perms ?? [])
        .filter(p => !(typeof p.streamId === 'string' && p.streamId.startsWith(':_cmc:')))
        .map(p => p.streamId != null
        ? { streamId: p.streamId, level: p.level ?? 'read' }
        : { feature: p.feature, setting: p.setting });
}
/**
 * Whether one stream can stop being shared on its own, and the permission set that would
 * remain. Pure: the model is only read to find the stream's parents; `opts.parentIds` adds the
 * parents of a stream the model does not know.
 */
function planStopSharing(perms, streamId, opts = {}) {
    const own = personPermissions(perms);
    const above = streamsAbove(streamId, opts.parentIds);
    if (own.some(p => p.streamId != null && above.has(p.streamId)))
        return { ok: false, reason: 'shared-through-parent' };
    if (!own.some(p => p.streamId === streamId))
        return { ok: false, reason: 'not-shared' };
    const kept = own.filter(p => p.streamId !== streamId);
    if (!kept.some(p => isDataStream(p.streamId))) {
        if (opts.allowChatOnly !== true)
            return { ok: false, reason: 'nothing-left' };
        if (!kept.some(p => p.streamId === 'hds-noop'))
            return { ok: true, newPermissions: [...kept, { streamId: 'hds-noop', level: 'read' }] };
    }
    return { ok: true, newPermissions: kept };
}
/**
 * What a requested permission set adds, takes away and changes the level of, compared with what
 * is shared now (the relationship's own channel and platform streams left out). For the request
 * card: a request for data already shared but at a higher level is a `changed` entry, not nothing.
 */
function scopeDiff(current, requested) {
    const levels = (perms) => new Map((perms ?? []).filter(p => isDataStream(p.streamId)).map(p => [p.streamId, p.level ?? 'read']));
    const now = levels(current);
    const asked = levels(requested);
    return {
        added: [...asked.keys()].filter(s => !now.has(s)),
        removed: [...now.keys()].filter(s => !asked.has(s)),
        changed: [...asked]
            .filter(([s, level]) => now.has(s) && now.get(s) !== level)
            .map(([s, level]) => ({ streamId: s, from: now.get(s) ?? 'read', to: level }))
    };
}
/**
 * Stop sharing one stream with a connection. Writes the scope update with
 * `content.hds = { removed: [streamId], message? }` and, by default, resolves once the grant
 * changed. Throws `HDSLibError` (`innerObject.reason`) when the stream cannot be stopped on its
 * own (`StopSharingRefusal`), when the relationship has no collectors stream, or when the
 * platform did not apply the change.
 */
async function stopSharingStream(connection, rel, streamId, opts = {}) {
    const collectorStreamId = requireCollectorStream(rel, 'stopSharingStream');
    const plan = planStopSharing(rel.grantedPermissions, streamId, opts);
    if ('reason' in plan) {
        throw new errors_ts_1.HDSLibError(`stopSharingStream: cannot stop sharing "${streamId}" on its own (${plan.reason})`, { reason: plan.reason, streamId });
    }
    const hds = { removed: [streamId], ...(opts.message ? { message: opts.message } : {}) };
    return writeScopeUpdate(connection, collectorStreamId, { accessId: rel.accessId, newPermissions: plan.newPermissions, hds }, opts);
}
/**
 * Share streams with a connection again (the person's own decision). Adds each stream not
 * shared now, at `opts.level` (default `read`), and writes `content.hds = { added, message? }`.
 * Throws `HDSLibError` when every stream is already shared, when the relationship has no
 * collectors stream, or when the platform did not apply the change.
 */
async function shareStreamsAgain(connection, rel, streamIds, opts = {}) {
    const collectorStreamId = requireCollectorStream(rel, 'shareStreamsAgain');
    const now = personPermissions(rel.grantedPermissions);
    const back = [...new Set(streamIds)].filter(id => isDataStream(id) && !now.some(p => p.streamId === id));
    if (back.length === 0) {
        throw new errors_ts_1.HDSLibError('shareStreamsAgain: every stream is already shared', { reason: 'already-shared', streamIds });
    }
    const level = opts.level ?? 'read';
    const newPermissions = [...now, ...back.map(streamId => ({ streamId, level }))];
    const hds = { added: back, ...(opts.message ? { message: opts.message } : {}) };
    return writeScopeUpdate(connection, collectorStreamId, { accessId: rel.accessId, newPermissions, hds }, opts);
}
/**
 * The relationship's sharing changes, oldest first: what the person stopped or shared again
 * (applied scope updates carrying `hds.removed` / `hds.added`) and what the connection asked
 * for (scope requests, with their answer). Read from the person's collectors stream.
 */
async function listSharingNotes(connection, collectorStreamId, opts = {}) {
    const events = await connection.apiOne('events.get', {
        streams: [collectorStreamId],
        types: [exports.ET_SCOPE_UPDATE, exports.ET_SCOPE_REQUEST],
        limit: opts.limit ?? 200
    }, 'events');
    return parseSharingNotes(events);
}
/** Pure part of `listSharingNotes` (exported for tests). `nowSec` decides expiry. */
function parseSharingNotes(events, nowSec = Date.now() / 1000) {
    const answers = new Map();
    for (const e of events ?? []) {
        const c = e.content ?? {};
        if (e.type !== exports.ET_SCOPE_UPDATE || typeof c.scopeRequestEventId !== 'string')
            continue;
        if (typeof c.applied !== 'boolean')
            continue;
        answers.set(c.scopeRequestEventId, { accepted: c.applied, id: e.id, asked: stringList(c.hds?.asked) });
    }
    const out = [];
    for (const e of events ?? []) {
        const c = e.content ?? {};
        const time = e.time ?? 0;
        if (e.type === exports.ET_SCOPE_UPDATE) {
            // answers are shown on their request; a change that was not applied is not a change
            if (typeof c.scopeRequestEventId === 'string' || c.applied !== true)
                continue;
            const removed = stringList(c.hds?.removed);
            const added = stringList(c.hds?.added);
            const base = { id: e.id, time, message: messageOf(c.hds?.message), peerNotified: c.status === 'completed' };
            if (removed && removed.length > 0)
                out.push({ kind: 'stopped', streamIds: removed, ...base });
            else if (added && added.length > 0)
                out.push({ kind: 'shared', streamIds: added, ...base });
        }
        else if (e.type === exports.ET_SCOPE_REQUEST && Array.isArray(c.newPermissions)) {
            const answered = answers.get(e.id);
            const expires = typeof c.expires === 'number' ? c.expires : null;
            let answer = null;
            if (answered)
                answer = answered.accepted ? 'accepted' : 'refused';
            else if (c.status === 'accepted' || c.status === 'refused')
                answer = c.status;
            else if (expires != null && expires < nowSec)
                answer = 'expired';
            out.push({
                kind: 'request',
                id: e.id,
                time,
                permissions: c.newPermissions,
                message: messageOf(c.message),
                expires,
                answer,
                answerEventId: answered?.id ?? null,
                asked: answered?.asked ?? null,
                formSpec: formSpecOf(c.hds),
                superseded: false
            });
        }
    }
    out.sort((a, b) => a.time - b.time);
    markSuperseded(out.filter((n) => n.kind === 'request'));
    return out;
}
// ---- data-set updates (data-model 3.41.0) ---- //
/** Default lifetime of a data-set update request, in seconds (30 days). */
exports.DATASET_REQUEST_TTL_SEC = 30 * 24 * 3600;
/**
 * Connection side: send an updated data set to one person, inside a scope request on the
 * relationship's own collectors stream. Every change goes this way, one within the current grant
 * included (`newPermissions` then equal to the grant). `openLink` is removed from the copy sent.
 *
 * Throws `HDSLibError` (`innerObject.reason`): `permissions-do-not-cover` when `newPermissions`
 * misses a permission the data set needs; `previous-request-in-flight` while an earlier request
 * of this stream is neither delivered nor failed nor expired (requests go out in order);
 * `delivery-failed` / `delivery-pending` (with `scopeRequestEventId`) after the write.
 */
async function proposeDataSetUpdate(connection, collectorStreamId, formSpec, opts = {}) {
    if (!collectorStreamId)
        throw new errors_ts_1.HDSLibError('proposeDataSetUpdate: collectorStreamId is required');
    const sent = (0, formSpec_ts_1.formSpecForPerson)(formSpec);
    const needed = (0, formSpec_ts_1.deriveCmcPermissions)(sent);
    const newPermissions = opts.newPermissions ?? needed;
    const missing = uncoveredPermissions(needed, newPermissions);
    if (missing.length > 0) {
        throw new errors_ts_1.HDSLibError('proposeDataSetUpdate: newPermissions do not cover the data set', { reason: 'permissions-do-not-cover', missing });
    }
    const nowSec = Date.now() / 1000;
    const previous = await listScopeRequests(connection, collectorStreamId);
    const inFlight = previous.find(r => r.delivery === 'pending' && r.answer !== 'expired');
    if (inFlight) {
        throw new errors_ts_1.HDSLibError('proposeDataSetUpdate: the previous request is not delivered yet', { reason: 'previous-request-in-flight', scopeRequestEventId: inFlight.id });
    }
    const formSpecHash = await (0, formSpec_ts_1.formSpecFingerprint)(sent);
    const hds = { formSpec: sent, formSpecHash };
    const content = {
        newPermissions,
        expires: opts.expires ?? Math.round(nowSec + exports.DATASET_REQUEST_TTL_SEC),
        hds
    };
    if (opts.message)
        content.message = opts.message;
    const event = await connection.apiOne('events.create', {
        streamIds: [collectorStreamId],
        type: exports.ET_SCOPE_REQUEST,
        content
    }, 'event');
    if (opts.waitForDelivery === false) {
        return { scopeRequestEventId: event.id, remoteScopeRequestEventId: null, status: 'pending', formSpecHash };
    }
    const until = Date.now() + (opts.deliveryTimeoutMs ?? 20000);
    const interval = opts.deliveryPollIntervalMs ?? 400;
    for (;;) {
        const ev = await connection.apiOne('events.getOne', { id: event.id }, 'event');
        const c = ev?.content ?? {};
        if (c.status === 'failed') {
            const reason = c.failure?.reason ?? 'delivery-failed';
            throw new errors_ts_1.HDSLibError(`proposeDataSetUpdate: request not delivered: ${reason}`, { reason: 'delivery-failed', failure: c.failure, scopeRequestEventId: event.id });
        }
        if (typeof c.remoteEventId === 'string') {
            return { scopeRequestEventId: event.id, remoteScopeRequestEventId: c.remoteEventId, status: c.status ?? 'delivered', formSpecHash };
        }
        if (Date.now() >= until)
            break;
        await new Promise(resolve => setTimeout(resolve, interval));
    }
    throw new errors_ts_1.HDSLibError('proposeDataSetUpdate: request not delivered yet', { reason: 'delivery-pending', scopeRequestEventId: event.id });
}
/**
 * Connection side: its scope requests on one collectors stream, oldest first, each joined with the
 * person's answer (the delivered `consent/scope-update-cmc` names the person's copy, which is the
 * request's `remoteEventId`).
 */
async function listScopeRequests(connection, collectorStreamId, opts = {}) {
    const events = await connection.apiOne('events.get', {
        streams: [collectorStreamId],
        types: [exports.ET_SCOPE_UPDATE, exports.ET_SCOPE_REQUEST],
        limit: opts.limit ?? 200
    }, 'events');
    return parseScopeRequests(events);
}
/** Pure part of `listScopeRequests` (exported for tests). `nowSec` decides expiry. */
function parseScopeRequests(events, nowSec = Date.now() / 1000) {
    const answers = new Map();
    for (const e of events ?? []) {
        const c = e.content ?? {};
        // the person's answers only: delivered here, so stamped with `from`
        if (e.type !== exports.ET_SCOPE_UPDATE || c.from == null || typeof c.scopeRequestEventId !== 'string' || typeof c.accept !== 'boolean')
            continue;
        answers.set(c.scopeRequestEventId, { accepted: c.accept, id: e.id, time: e.time ?? 0, reason: messageOf(c.reason), asked: stringList(c.hds?.asked) });
    }
    const out = [];
    for (const e of events ?? []) {
        const c = e.content ?? {};
        // the connection's own requests: no `from`
        if (e.type !== exports.ET_SCOPE_REQUEST || c.from != null || !Array.isArray(c.newPermissions))
            continue;
        const remoteEventId = typeof c.remoteEventId === 'string' ? c.remoteEventId : null;
        const answered = remoteEventId != null ? answers.get(remoteEventId) : undefined;
        const expires = typeof c.expires === 'number' ? c.expires : null;
        const formSpec = formSpecOf(c.hds);
        let answer = null;
        if (answered)
            answer = answered.accepted ? 'accepted' : 'refused';
        else if (expires != null && expires < nowSec)
            answer = 'expired';
        out.push({
            id: e.id,
            time: e.time ?? 0,
            permissions: c.newPermissions,
            message: messageOf(c.message),
            expires,
            remoteEventId,
            delivery: remoteEventId != null ? 'delivered' : c.status === 'failed' ? 'failed' : 'pending',
            formSpec,
            formSpecHash: formSpec != null && typeof c.hds?.formSpecHash === 'string' ? c.hds.formSpecHash : null,
            answer,
            answeredAt: answered?.time ?? null,
            answerEventId: answered?.id ?? null,
            reason: answered?.reason ?? null,
            asked: answered?.asked ?? null,
            superseded: false
        });
    }
    out.sort((a, b) => a.time - b.time);
    markSuperseded(out.filter(r => r.delivery !== 'failed'));
    return out;
}
/**
 * Connection side: the `formSpecHash` of the data set a person holds through scope requests: the
 * one of the request carrying a data set whose acceptance is the most recent. Null when she never
 * accepted one: she then holds the invite's snapshot.
 */
function acceptedDataSetHash(requests) {
    let best = null;
    for (const r of requests ?? []) {
        if (r.answer !== 'accepted' || r.formSpecHash == null)
            continue;
        if (best == null || (r.answeredAt ?? 0) > (best.answeredAt ?? 0))
            best = r;
    }
    return best?.formSpecHash ?? null;
}
/**
 * Person side: accept a connection's scope request and, when it carries a data set, replace her
 * copy (`hdsFormSpec` on her `consent/accept-cmc` event `acceptEventId`) once the grant changed.
 * Throws as `cmc.acceptScopeUpdate` does when nothing was applied; the copy is then untouched.
 */
async function acceptDataSetUpdate(connection, request, acceptEventId, opts = {}) {
    const res = await patchedPryv_ts_1.cmc.acceptScopeUpdate(connection, request.id, opts);
    let dataSetUpdated = false;
    if (request.formSpec != null && acceptEventId != null) {
        await (0, formSpec_ts_1.mirrorFormSpecOnAcceptEvent)(connection, acceptEventId, (0, formSpec_ts_1.formSpecForPerson)(request.formSpec));
        dataSetUpdated = true;
    }
    return {
        updateAcceptEventId: res.updateAcceptEventId,
        newPermissions: Array.isArray(res.newPermissions) ? res.newPermissions : [],
        peerNotified: res.peerNotified !== false,
        dataSetUpdated
    };
}
/**
 * The permissions of `needed` that `granted` does not cover: a stream is covered by itself, a
 * parent in the data model or `*`, at a level that includes the needed one.
 */
function uncoveredPermissions(needed, granted) {
    return (needed ?? []).filter(n => {
        if (n.streamId == null)
            return !(granted ?? []).some(g => g.feature === n.feature && g.setting === n.setting);
        const above = streamsAbove(n.streamId);
        return !(granted ?? []).some(g => g.streamId != null &&
            (g.streamId === n.streamId || above.has(g.streamId)) &&
            levelIncludes(g.level ?? 'read', n.level ?? 'read'));
    });
}
// ---- internals ---- //
function requireCollectorStream(rel, fn) {
    if (!rel?.accessId)
        throw new errors_ts_1.HDSLibError(`${fn}: the relationship's accessId is required`);
    if (!rel.localCollectorStreamId) {
        throw new errors_ts_1.HDSLibError(`${fn}: the relationship has no collectors stream (no system channel)`, { reason: 'no-collector-stream', accessId: rel.accessId });
    }
    return rel.localCollectorStreamId;
}
function stringList(v) {
    return Array.isArray(v) ? v.filter((s) => typeof s === 'string') : null;
}
function messageOf(v) {
    if (v == null || typeof v !== 'object' || Array.isArray(v))
        return null;
    return v;
}
function formSpecOf(hds) {
    const f = hds?.formSpec;
    return f != null && typeof f === 'object' && Array.isArray(f.sections) ? f : null;
}
/**
 * An unanswered request carrying a data set is superseded once a later one (`time`) carries a data
 * set, answered or not: accepting it would bring back an older version (data-model 3.41.1).
 * `requests` is sorted oldest first.
 */
function markSuperseded(requests) {
    const withDataSet = requests.filter(r => r.formSpec != null);
    for (const r of withDataSet.slice(0, -1)) {
        if (r.answer == null)
            r.superseded = true;
    }
}
const LEVEL_INCLUDES = {
    manage: ['manage', 'contribute', 'read', 'create-only'],
    contribute: ['contribute', 'read', 'create-only'],
    read: ['read'],
    'create-only': ['create-only']
};
function levelIncludes(have, need) {
    return (LEVEL_INCLUDES[have] ?? [have]).includes(need);
}
async function writeScopeUpdate(connection, collectorStreamId, content, opts) {
    const event = await connection.apiOne('events.create', {
        streamIds: [collectorStreamId],
        type: exports.ET_SCOPE_UPDATE,
        content
    }, 'event');
    if (opts.waitForCompletion === false) {
        return { updateEventId: event.id, status: 'pending', applied: false, newPermissions: content.newPermissions, peerNotified: false };
    }
    const outcome = await waitForOutcome(connection, event.id, opts);
    if (outcome.applied !== true) {
        const reason = outcome.failure?.reason ?? 'not-applied';
        throw new errors_ts_1.HDSLibError(`scope update not applied: ${reason}`, { reason, updateEventId: event.id, failure: outcome.failure });
    }
    const result = {
        updateEventId: event.id,
        status: outcome.status ?? 'unknown',
        applied: true,
        newPermissions: Array.isArray(outcome.newPermissions) ? outcome.newPermissions : content.newPermissions,
        peerNotified: outcome.status === 'completed'
    };
    if (outcome.status === 'failed')
        result.deliveryFailure = outcome.failure;
    return result;
}
/**
 * Poll the trigger until the platform settled it (`completed` / `failed`). On timeout, an outcome
 * already recorded (`applied` true or false) is returned as is; otherwise throw: the change may
 * still happen, so it is not reported as failed.
 */
async function waitForOutcome(connection, eventId, opts) {
    const until = Date.now() + (opts.completionTimeoutMs ?? 20000);
    const interval = opts.completionPollIntervalMs ?? 400;
    let content = {};
    for (;;) {
        const ev = await connection.apiOne('events.getOne', { id: eventId }, 'event');
        content = ev?.content ?? {};
        if (content.status === 'completed' || content.status === 'failed')
            return content;
        if (Date.now() >= until)
            break;
        await new Promise(resolve => setTimeout(resolve, interval));
    }
    if (typeof content.applied === 'boolean')
        return content;
    throw new errors_ts_1.HDSLibError('scope update outcome not known yet', { reason: 'outcome-unknown', updateEventId: eventId, lastStatus: content.status });
}
